import { ArrowUpDown, Calendar, ChevronDown, Search } from "lucide-react";
import { cn } from "@/utils/cn";
import type { MultiSelectOption } from "@/components/ui/MultiSelect";
import { APPROVAL_STAGES } from "./stageUtils";
import type { DashboardFilterValues } from "./dashboardFilters";

interface Props {
  values: DashboardFilterValues;
  onChange: (values: DashboardFilterValues) => void;
  fornecedorOptions: MultiSelectOption[];
}

export function SolicitacaoPagamentoDashboardFilterBar({
  values,
  onChange,
  fornecedorOptions,
}: Props) {
  return (
    <div className="flex flex-wrap items-center gap-2 md:gap-3">
      <div className="relative min-w-[140px] flex-shrink-0">
        <select
          value={values.etapaFilter}
          onChange={(e) =>
            onChange({
              ...values,
              etapaFilter: e.target.value as DashboardFilterValues["etapaFilter"],
            })
          }
          className={cn(
            "h-9 w-full appearance-none rounded-lg border border-slate-200 bg-white pl-3 pr-8 text-xs text-slate-700",
            "focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600",
          )}
        >
          <option value="">Todas as etapas</option>
          {APPROVAL_STAGES.map((stage) => (
            <option key={stage.id} value={stage.id}>
              {stage.step}. {stage.title}
            </option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
      </div>

      <div className="relative min-w-[160px] flex-shrink-0">
        <select
          value={values.fornecedorFilter}
          onChange={(e) => onChange({ ...values, fornecedorFilter: e.target.value })}
          className={cn(
            "h-9 w-full appearance-none rounded-lg border border-slate-200 bg-white pl-3 pr-8 text-xs text-slate-700",
            "focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600",
          )}
        >
          <option value="">Todos os fornecedores</option>
          {fornecedorOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
      </div>

      <div className="relative min-w-[120px] flex-shrink-0">
        <Calendar className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
        <input
          type="date"
          value={values.dateFrom}
          onChange={(e) => onChange({ ...values, dateFrom: e.target.value })}
          className={cn(
            "h-9 w-full rounded-lg border border-slate-200 bg-white pl-8 pr-2 text-xs text-slate-700",
            "focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600",
            !values.dateFrom && "text-transparent",
          )}
          title="Data inicial"
        />
        {!values.dateFrom && (
          <span className="pointer-events-none absolute left-8 top-1/2 -translate-y-1/2 text-xs text-slate-400">
            Período
          </span>
        )}
      </div>

      <div className="relative min-w-[120px] flex-shrink-0 hidden sm:block">
        <input
          type="date"
          value={values.dateTo}
          onChange={(e) => onChange({ ...values, dateTo: e.target.value })}
          className={cn(
            "h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-xs text-slate-700",
            "focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600",
            !values.dateTo && "text-transparent",
          )}
          title="Data final"
        />
        {!values.dateTo && (
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
            até
          </span>
        )}
      </div>

      <div className="relative min-w-[180px] flex-1">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          value={values.search}
          onChange={(e) => onChange({ ...values, search: e.target.value })}
          placeholder="Buscar solicitação..."
          autoComplete="off"
          className={cn(
            "h-9 w-full rounded-lg border border-slate-200 bg-white pl-8 pr-3 text-xs text-slate-700",
            "placeholder:text-slate-400 focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600",
          )}
        />
      </div>

      <div className="relative min-w-[140px] flex-shrink-0 ml-auto">
        <ArrowUpDown className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
        <select
          value={values.sortOrder}
          onChange={(e) =>
            onChange({ ...values, sortOrder: e.target.value as DashboardFilterValues["sortOrder"] })
          }
          className={cn(
            "h-9 w-full appearance-none rounded-lg border border-slate-200 bg-white pl-8 pr-8 text-xs text-slate-700",
            "focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600",
          )}
        >
          <option value="recent">Mais recentes</option>
          <option value="oldest">Mais antigas</option>
          <option value="valor_desc">Maior valor</option>
          <option value="valor_asc">Menor valor</option>
        </select>
        <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
      </div>
    </div>
  );
}
